import { COMPARISONS, USE_CASES } from './marketingContent.js';

/**
 * What the SPA serves, described once for everything that has to agree with it.
 *
 * Three consumers read this: the sitemap, the build step that gives each
 * public page its own <head>, and the edge function that decides whether a
 * path is a route at all. They used to be able to drift silently — a page
 * missing from the sitemap is invisible, and a route missing from the edge
 * list is a 404 on a page that exists. Tests hold all three to this file, and
 * `routes.appRoutes.test.ts` holds this file to App.tsx.
 */

/**
 * A comparison or use-case page as a route.
 *
 * Derived rather than listed again: those pages already define their own path,
 * title and description next to their copy, and repeating them here is exactly
 * how a page ends up in the sitemap under a URL the router does not serve.
 */
function pageRoute(page: { path: string; title: string; description: string }): MarketingRoute {
  return {
    path: page.path,
    file: `${page.path.slice(1)}.html`,
    title: page.title,
    description: page.description,
  };
}

export interface MarketingRoute {
  /** The URL, exactly as it appears in the router and the sitemap. */
  path: string;
  /** The prerendered file this path is served from, relative to the bundle. */
  file: string;
  /** 50-60 characters. Google rewrites anything longer, and did. */
  title: string;
  /** 150-160 characters, written to earn the click rather than describe. */
  description: string;
  /** Emit a robots noindex. Only the not-found page sets this. */
  noindex?: boolean;
}

/**
 * Public pages worth indexing, each with the head it should carry.
 *
 * Ordered as a visitor would meet them: the pitch, the two comparisons someone
 * arrives on from a competitor's name, the four rooms the product is used in,
 * then the legal pages.
 */
export const MARKETING_ROUTES: MarketingRoute[] = [
  {
    path: '/',
    file: 'index.html',
    title: 'FloorPoll — live polls your audience joins in one tap',
    description:
      'Build a poll, put it on the big screen, and watch the room vote from their phones. Anonymous by default. No app, no account, no downloads.',
  },
  ...[...COMPARISONS, ...USE_CASES].map(pageRoute),
  {
    path: '/privacy',
    file: 'privacy.html',
    title: 'Privacy Policy — FloorPoll',
    description:
      'What FloorPoll collects and what it deliberately does not. Voters give no name, email, phone number or location to answer a poll.',
  },
  {
    path: '/terms',
    file: 'terms.html',
    title: 'Terms of Service — FloorPoll',
    description:
      'The terms covering FloorPoll accounts, workspaces, and the polls you run on them, including what an audience agrees to by answering anonymously.',
  },
];

/**
 * The page CloudFront serves, with a 404 on it, when nothing matches.
 *
 * Kept out of MARKETING_ROUTES so it cannot reach the sitemap: a sitemap that
 * advertises the not-found page is a sitemap describing a page that should
 * never be indexed. It carries its own noindex for the same reason, which is
 * what the SPA's not-found view does at runtime too — this is the version a
 * crawler sees without executing anything.
 */
export const NOT_FOUND_ROUTE: MarketingRoute = {
  path: '/404',
  file: '404.html',
  title: 'Page not found — FloorPoll',
  description:
    'That address does not match a page on FloorPoll. Check the link, or head back to the home page to build a poll and put it in front of a room.',
  noindex: true,
};

/** Just the paths, for the sitemap and for anything checking membership. */
export const MARKETING_PATHS = MARKETING_ROUTES.map((route) => route.path);

/**
 * Every path pattern the SPA answers to, as regular-expression sources.
 *
 * This is the allowlist the edge consults before deciding a path does not
 * exist, so a route missing here is a live page returning 404. It therefore
 * covers the whole router, not only the public part: the console routes are
 * behind an auth gate in the app, but they are still real URLs that must
 * resolve on a hard refresh.
 */
export const APP_ROUTE_PATTERNS: string[] = [
  '^/$',
  ...MARKETING_PATHS.filter((path) => path !== '/').map((path) => `^${path}$`),

  // Auth, all static.
  '^/signin$',
  '^/signup$',
  '^/verify$',
  '^/forgot-password$',
  '^/reset-password$',
  '^/auth/callback$',

  /**
   * Unsubscribe, reached from a link in a lifecycle email.
   *
   * Deliberately here and deliberately *not* in MARKETING_ROUTES: it must
   * resolve, because every load of it is a hard load from a mail client, and
   * it must never be indexed or advertised in the sitemap. It carries no
   * content worth finding, and the URL it is useful at always has a
   * per-user token on it.
   */
  '^/unsubscribe$',

  // Audience surfaces. `/j` with no code is the enter-a-code screen.
  '^/j$',
  '^/j/[^/]+$',
  '^/screen/[^/]+$',
  '^/invite/[^/]+$',

  // Console.
  '^/polls$',
  '^/polls/[^/]+$',
  '^/polls/[^/]+/results$',
  '^/polls/[^/]+/present$',
  '^/runs/[^/]+$',
  '^/people$',
  '^/settings$',
  '^/account$',
  '^/usage$',

  // Bookmarks predating the quiz to poll rename, which the app still
  // redirects. Dropping this would 404 links that work today.
  '^/quizzes(/.*)?$',
];
